import { gql } from '@apollo/client';

/**
 * Get payment dashboard statistics
 */
export const GET_PAYMENT_STATS = gql`
  query GetPaymentStats($startDate: Timestamp, $endDate: Timestamp) {
    paymentStats(startDate: $startDate, endDate: $endDate) {
      totalVolume
      totalTransactions
      successRate
      averageAmount
      volumeByProvider {
        provider
        volume
        count
        successRate
      }
      volumeByMethod {
        method
        volume
        count
      }
      recentTrend {
        date
        volume
        count
      }
    }
  }
`;

/**
 * Get list of transactions for admin
 */
export const GET_ADMIN_TRANSACTIONS = gql`
  query GetAdminTransactions(
    $filter: TransactionFilterInput
    $sort: TransactionSortInput
    $pagination: PaginationInput
  ) {
    adminTransactions(filter: $filter, sort: $sort, pagination: $pagination) {
      data {
        id
        orderId
        amount
        currency
        status
        provider
        paymentMethod
        type
        description
        user {
          id
          email
          profile {
            displayName
          }
        }
        walletAmount
        cashAmount
        fees
        providerTransactionId
        metadata
        errorMessage
        createdAt
        updatedAt
        confirmedAt
      }
      total
      page
      pageSize
    }
  }
`;

/**
 * Get single transaction details
 */
export const GET_TRANSACTION_DETAILS = gql`
  query GetTransactionDetails($id: String!) {
    transaction(id: $id) {
      id
      orderId
      amount
      currency
      status
      provider
      paymentMethod
      type
      description
      user {
        id
        email
        profile {
          displayName
          phoneNumber
        }
      }
      walletAmount
      cashAmount
      fees
      providerTransactionId
      metadata
      errorMessage
      webhookHistory {
        id
        event
        payload
        receivedAt
        processed
        processingError
      }
      refunds {
        id
        amount
        reason
        status
        processedAt
        createdAt
      }
      createdAt
      updatedAt
      confirmedAt
    }
  }
`;

/**
 * Get provider comparison data
 */
export const GET_PROVIDER_COMPARISON = gql`
  query GetProviderComparison($startDate: Timestamp, $endDate: Timestamp) {
    providerComparison(startDate: $startDate, endDate: $endDate) {
      providers {
        provider
        totalVolume
        totalTransactions
        successfulTransactions
        failedTransactions
        successRate
        averageProcessingTime
        fees
      }
      dailyComparison {
        date
        cinetpay {
          volume
          count
        }
        fedapay {
          volume
          count
        }
        wallet {
          volume
          count
        }
      }
    }
  }
`;

/**
 * Get failed webhooks for alerts
 */
export const GET_FAILED_WEBHOOKS = gql`
  query GetFailedWebhooks($limit: Int) {
    failedWebhooks(limit: $limit) {
      id
      provider
      event
      payload
      receivedAt
      processingError
      retryCount
      transactionId
    }
  }
`;

/**
 * Get payment method distribution
 */
export const GET_PAYMENT_METHOD_DISTRIBUTION = gql`
  query GetPaymentMethodDistribution($startDate: Timestamp, $endDate: Timestamp) {
    paymentMethodDistribution(startDate: $startDate, endDate: $endDate) {
      method
      label
      volume
      count
      percentage
    }
  }
`;

/**
 * Retry failed webhook
 */
export const RETRY_WEBHOOK = gql`
  mutation RetryWebhook($webhookId: String!) {
    retryWebhook(webhookId: $webhookId) {
      success
      error
    }
  }
`;

/**
 * Admin refund a payment
 */
export const ADMIN_REFUND_PAYMENT = gql`
  mutation AdminRefundPayment($paymentId: String!, $amount: Int, $reason: String!) {
    adminRefundPayment(paymentId: $paymentId, amount: $amount, reason: $reason) {
      success
      refundId
      error
    }
  }
`;

// ============================================================================
// PAYMENT CONFIGURATION QUERIES & MUTATIONS (PAY-008)
// ============================================================================

/**
 * Get all provider configurations
 */
export const GET_PROVIDER_CONFIGS = gql`
  query GetProviderConfigs {
    paymentProviderConfigs {
      id
      provider
      isEnabled
      isDefault
      priority
      environment
      minAmount
      maxAmount
      commissionRate
      fixedFee
      activeCountries
      activeMethods
      successRate
      avgResponseTime
      lastHealthCheck
      createdAt
      updatedAt
    }
  }
`;

/**
 * Update provider configuration
 */
export const UPDATE_PROVIDER_CONFIG = gql`
  mutation UpdateProviderConfig($provider: PaymentProvider!, $input: UpdateProviderConfigInput!) {
    updatePaymentProviderConfig(provider: $provider, input: $input) {
      id
      provider
      isEnabled
      isDefault
      priority
      environment
      minAmount
      maxAmount
      commissionRate
      fixedFee
      activeCountries
      activeMethods
      updatedAt
    }
  }
`;

/**
 * Get all hybrid payment limits
 */
export const GET_HYBRID_LIMITS = gql`
  query GetHybridLimits {
    hybridPaymentLimits {
      id
      paymentType
      maxEkaaPercentage
      maxEkaaAmount
      minCashAmount
      isEnabled
      createdAt
      updatedAt
    }
  }
`;

/**
 * Update hybrid payment limit
 */
export const UPDATE_HYBRID_LIMIT = gql`
  mutation UpdateHybridLimit($paymentType: MpPaymentType!, $input: UpdateHybridLimitInput!) {
    updateHybridPaymentLimit(paymentType: $paymentType, input: $input) {
      id
      paymentType
      maxEkaaPercentage
      maxEkaaAmount
      minCashAmount
      isEnabled
      updatedAt
    }
  }
`;

/**
 * Get all country provider routings
 */
export const GET_COUNTRY_ROUTINGS = gql`
  query GetCountryRoutings {
    countryProviderRoutings {
      id
      countryCode
      primaryProvider
      fallbackProvider
      availableMethods
      isEnabled
      notes
      createdAt
      updatedAt
    }
  }
`;

/**
 * Create country routing
 */
export const CREATE_COUNTRY_ROUTING = gql`
  mutation CreateCountryRouting($input: CreateCountryRoutingInput!) {
    createCountryProviderRouting(input: $input) {
      id
      countryCode
      primaryProvider
      fallbackProvider
      availableMethods
      isEnabled
      notes
      createdAt
    }
  }
`;

/**
 * Update country routing
 */
export const UPDATE_COUNTRY_ROUTING = gql`
  mutation UpdateCountryRouting($countryCode: String!, $input: UpdateCountryRoutingInput!) {
    updateCountryProviderRouting(countryCode: $countryCode, input: $input) {
      id
      countryCode
      primaryProvider
      fallbackProvider
      availableMethods
      isEnabled
      notes
      updatedAt
    }
  }
`;

/**
 * Delete country routing
 */
export const DELETE_COUNTRY_ROUTING = gql`
  mutation DeleteCountryRouting($countryCode: String!) {
    deleteCountryProviderRouting(countryCode: $countryCode)
  }
`;

/**
 * Get payment config audit logs
 */
export const GET_CONFIG_AUDIT_LOGS = gql`
  query GetConfigAuditLogs($filter: AuditLogFilterInput) {
    paymentConfigAuditLogs(filter: $filter) {
      data {
        id
        action
        entity
        entityId
        userId
        userEmail
        previousValue
        newValue
        ipAddress
        userAgent
        createdAt
      }
      total
    }
  }
`;

/**
 * Clear payment config cache
 */
export const CLEAR_CONFIG_CACHE = gql`
  mutation ClearConfigCache {
    clearPaymentConfigCache
  }
`;
